/**
 * ZoomPanControls Component
 * Feature: 012-svg-zoom-pan
 * Package: @alchemiesolns/svg-interactive (copied from svg-container)
 *
 * Interactive control panel for zoom and pan operations.
 * Renders as HTML buttons positioned absolutely outside the SVG viewport.
 */

import { useRef } from 'react';
import '@alchemiesolns/shared-styles/zoom-pan-controls.css';
import { useAnnouncer } from '@alchemiesolns/announcer';
import type { ZoomPanControls } from '../types/useZoomPan.types';

export interface ZoomPanControlsProps {
  /** Control functions from useZoomPan hook */
  controls: ZoomPanControls;

  /** Button size in pixels (default: 32) */
  buttonSize?: number;

  /** Zoom step multiplier (default: 1.2 for zoom in, 0.833 for zoom out) */
  zoomStep?: number;

  /** Custom class name for styling */
  className?: string;

  /** Position from left edge (default: 16px) */
  left?: number;

  /** Position from bottom edge (default: 16px) */
  bottom?: number;

  /** Position from right edge (default: undefined) */
  right?: number;
}

/**
 * ZoomPanControls - Interactive control panel for zoom/pan operations
 *
 * Renders as HTML buttons positioned absolutely in the lower-left corner.
 * Does not zoom/pan with the SVG content.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { controls, viewBoxString, svgRef } = useZoomPan({
 *     initialViewBox: '0 0 400 300'
 *   });
 *
 *   return (
 *     <div style={{ position: 'relative' }}>
 *       <svg ref={svgRef} viewBox={viewBoxString}>
 *         {/* Your SVG content *\/}
 *       </svg>
 *       <ZoomPanControls controls={controls} />
 *     </div>
 *   );
 * }
 * ```
 */
export function ZoomPanControls({
  controls,
  buttonSize =24,
  zoomStep = 1.2,
  className = '',
  left = undefined,
  bottom = undefined,
  right = undefined,
}: ZoomPanControlsProps) {
  const { announceAssertive } = useAnnouncer();

  // Track the user-driven zoom level locally. useZoomPan rebases its internal
  // zoomFactor whenever the workspace layout recalculates (which happens on
  // every zoom because the layout responds to viewBox), so reading
  // state.zoomFactor from the hook gives a value that resets toward 1 between
  // clicks. The local ref mirrors the button presses directly, which is what
  // users expect: "Set to X%" relative to the last fit-to-screen.
  const localZoomFactorRef = useRef(1);

  const handleZoomIn = () => {
    localZoomFactorRef.current *= zoomStep;
    controls.zoom(zoomStep, 0.5, 0.5);
    announceAssertive(
      `Workspace zoomed in. Set to ${Math.round(localZoomFactorRef.current * 100)}%.`
    );
  };

  const handleZoomOut = () => {
    localZoomFactorRef.current /= zoomStep;
    controls.zoom(1 / zoomStep, 0.5, 0.5);
    announceAssertive(
      `Workspace zoomed out. Set to ${Math.round(localZoomFactorRef.current * 100)}%.`
    );
  };

  const handleReset = () => {
    localZoomFactorRef.current = 1;
    controls.reset();
    announceAssertive('Workspace fit to screen.');
  };

  const buttons = [
    { id: 'zoom-in', cssClass: 'player-light-btn-first', icon:
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4.8 12.2182H12.2182M12.2182 12.2182H19.6364M12.2182 12.2182V4.80005M12.2182 12.2182V19.6364" stroke="#232323" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      , label: 'Zoom In', onClick: handleZoomIn },
    { id: 'reset', cssClass: 'player-light-btn-middle', icon:
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 8H16V12" stroke="#232323" strokeWidth="1.75"/>
        <path d="M12 16H8V12" stroke="#232323" strokeWidth="1.75"/>
        <mask id="path-3-inside-1_215_28" fill="white">
        <rect x="4" y="4" width="16" height="16" rx="1"/>
        </mask>
        <rect x="4" y="4" width="16" height="16" rx="1" stroke="#232323" strokeWidth="3.5" mask="url(#path-3-inside-1_215_28)"/>
      </svg>
      , label: 'Fit-to-screen', onClick: handleReset },
    { id: 'zoom-out', cssClass: 'player-light-btn-last', icon:
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4.8 12.2183H12.2182H19.6364" stroke="#232323" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      , label: 'Zoom Out', onClick: handleZoomOut },
  ];

  return (
    <div
      className={`zoom-pan-controls ${className}`}
      role="group"
      aria-label="Zoom Controls"
      style={{
        left: `${left === undefined ? "" : left + "px"}`,
        right: `${right === undefined ? "" : right + "px"}`,
        bottom: `${bottom === undefined ? "" : bottom + "px"}`,
        position: `${left || right || bottom ? "absolute" : "relative"}`,
        height: `${left || right || bottom ? "" : "fit-content"}`,
        alignItems: `${left || right || bottom ? "flex-end" : "center"}`,
        overflow: `${left || right || bottom ? "hidden" : "visible"}`,
        margin: `${left || right || bottom ? "" : "4px"}`,

      }}
    >
      {buttons.map((button) => (
        <button
          key={button.id}
          onClick={button.onClick}
          aria-label={button.label}
          title={button.label}
          className={`player-light-btn ${button.cssClass}`}
          style={{
            width: `${buttonSize}px`,
            height: `${buttonSize}px`,
          }}
        >
          {button.icon}
        </button>
      ))}
    </div>
  );
}
